import axios from 'axios';
import * as Sentry from '@sentry/react';
import { variablesGlobales } from '../configs/variables';

const REQUEST_TIMEOUT_MS = 30000;
const RETRY_DELAY_MS = 1000;
const TIMEOUT_CODES = new Set(['ECONNABORTED', 'ETIMEDOUT']);
// 401 y 404 se usan como flujo normal (sesión vencida, recurso inexistente)
const IGNORED_STATUS = new Set([401, 404]);

const isNetworkOrTimeout = (error) =>
  !error?.response && (error?.code === 'ERR_NETWORK' || TIMEOUT_CODES.has(error?.code));

// Ruta sin query string: el query puede traer documento, correo o teléfono
const safePath = (error) => {
  const raw = `${error?.config?.url ?? ''}`;
  return raw.split('?')[0];
};

const getErrorType = (error) => {
  const status = error?.response?.status;
  if (status >= 500) return '5xx';
  if (status >= 400) return '4xx';
  if (TIMEOUT_CODES.has(error?.code)) return 'timeout';
  if (error?.code === 'ERR_NETWORK') return 'red';
  return null;
};

const reportError = (error) => {
  try {
    if (axios.isCancel(error)) return;
    const status = error?.response?.status;
    if (IGNORED_STATUS.has(status)) return;
    const type = getErrorType(error);
    if (!type) return;
    const method = `${error?.config?.method ?? 'get'}`.toUpperCase();
    const path = safePath(error);
    // Error nuevo y sin config: evita enviar la URL completa ni el cuerpo
    const report = new Error(`HTTP ${type} ${status ?? error?.code} ${method} ${path}`);
    Sentry.withScope((scope) => {
      scope.setTag('http_error_type', type);
      scope.setTag('http_status', `${status ?? 'sin_respuesta'}`);
      scope.setTag('http_path', path);
      scope.setFingerprint(['http-error', type, `${status ?? error?.code}`, method, path]);
      Sentry.captureException(report);
    });
  } catch (sentryError) {
    // Nunca debe romper la petición original por un fallo de Sentry/GlitchTip
  }
};

// Un reintento con espera, solo para GET ante error de red o timeout
export const attachGetRetry = (axiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config;
      const isGet = `${config?.method ?? ''}`.toLowerCase() === 'get';
      if (config && isGet && !config.__retried && isNetworkOrTimeout(error)) {
        config.__retried = true;
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        return axiosInstance.request(config);
      }
      return Promise.reject(error);
    }
  );
};

const getInstanceAxios = () => {
  const axiosInstance = axios.create({
    baseURL: variablesGlobales.urlServiceBackend,
    withCredentials: true, // Incluye las credenciales (cookies) en la solicitud
    timeout: REQUEST_TIMEOUT_MS
  });

  attachGetRetry(axiosInstance);

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      reportError(error);
      return Promise.reject(error);
    }
  );

  return axiosInstance
}



export default getInstanceAxios;
